import React, { useEffect, useState } from 'react';
import { encryptStorage } from '../../constant';
import { PostRegistration, GetSessions } from '../../service/SessionAPI';
import { Toast } from './Toast';

const EditParticipants = ({
    participant,
    sessionId,
    empId: externalEmpId,
    fetchSessions,
    onClose
}) => {
    const empId = externalEmpId || encryptStorage.getItem('employeeId');
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchAvailableSlots = async () => {
            try {
                const response = await GetSessions(empId);
                const fetchedSlots = response.data.data.sessions
                    .filter(session => session.session_id === sessionId)
                    .flatMap(session => session.slots);
                setAvailableSlots(fetchedSlots);
                const foundSlot = fetchedSlots.find(slots => slots.slot_id === participant.slot_id);
                setSelectedSlot(foundSlot || fetchedSlots[0]);
            } catch (error) {
                console.error("Error fetching sessions:", error);
                Toast.error("Failed to fetch available slots.");
            }
        };
        fetchAvailableSlots();
    }, [empId, participant, sessionId]);

    const parseDaysLeft = (daysLeft) => {
        if (daysLeft === "Past") return -1;
        if (typeof daysLeft === "string" && daysLeft.includes("days")) {
            return parseInt(daysLeft.split(" ")[0], 10);
        }
        if (typeof daysLeft === "string" && daysLeft.includes("month")) {
            return 30; // Approximate a month as 30 days
        }
        return 999; // Future date
    };

    const getOtherValidSlots = () => {
        return availableSlots
            .filter(slot => {
                const isActive = slot.is_active;
                const daysLeft = parseDaysLeft(slot.days_left);
                const hasAvailableSeats = slot.registered_count < slot.seat_available;
                return (
                    isActive &&
                    daysLeft > 7 &&
                    hasAvailableSeats &&
                    slot.participant_id === ""
                );
            })
            .map(slot => {
                const totalSeats = slot.registered_count + slot.seat_available;
                const percentage = totalSeats > 0 ? (slot.registered_count / slot.seat_available) * 100 : 0;

                if (percentage === 100) {
                    slot.seatClass = 'seats-closed';
                } else if (percentage >= 81 && percentage < 100) {
                    slot.seatClass = 'seats-few';
                } else if (percentage >= 51 && percentage < 81) {
                    slot.seatClass = 'seats-limited';
                } else {
                    slot.seatClass = 'seats-available';
                }


                // Add percentage to slot data
                return {
                    ...slot,
                };
            });
    };

    const formatSessionDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day)); // treat as UTC date, avoid timezone offset
        const options = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString('en-GB', options);
    };

    const handleConfirmReschedule = async () => {
        if (!selectedSlot) {
            Toast.error("Please select a slot before rescheduling.");
            return;
        }
        setLoading(true);
        const payload = {
            session_id: sessionId,
            slot_id: selectedSlot.slot_id,
            emp_id: empId,
            participant_id: participant.participant_id,
            status: 'Reschedule'
        };
        try {
            const res = await PostRegistration(payload);
            if (res.data.code === 200) {
                Toast.success("Session Rescheduled Successfully");
                fetchSessions && fetchSessions();
                onClose && onClose();
            } else {
                Toast.error("Failed to Reschedule the session");
            }
        } catch (error) {
            console.error("Error during rescheduling:", error);
            Toast.error("Failed to Reschedule the session");
        } finally {
            setLoading(false);
        }
    };


    // -- Modal Renderers --
    const renderRescheduleModal = () => {

        return (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                <div className="modal-dialog sessions-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h6 className="modal-title">Edit Registration - {participant.emp_name}</h6>
                            <button type="button" className="btn-close font-12" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-12 mb-4">
                                    <div className="session-dates-toggle d-flex flex-wrap gap-2">
                                        {otherValidSlots.map((slot, index) => {
                                            const isDisabled = slot.seatClass === 'seats-closed';
                                            return (
                                                <React.Fragment key={index}>
                                                    <input
                                                        type="radio"
                                                        className="btn-check"
                                                        name="reschedule-radio-grp"
                                                        id={`rescheduleRadio${index}`}
                                                        disabled={isDisabled}
                                                        checked={selectedSlot?.slot_id === slot.slot_id}
                                                        onChange={() => !isDisabled && setSelectedSlot(slot)}
                                                    />
                                                    <label
                                                        className={`btn font-11 semibold-text ${slot.seatClass}`}
                                                        htmlFor={`rescheduleRadio${index}`}
                                                    >
                                                        {formatSessionDate(slot.session_date)}
                                                    </label>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                                {selectedSlot && (
                                    <>
                                        <div className="col-md-6 pe-2">
                                            <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                                <p className="font-12 regular-text grey-text mb-1">Timings</p>
                                                <p className="font-12 semibold-text mb-0">{`${selectedSlot.start_time} - ${selectedSlot.end_time}`}</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6 ps-2">
                                            <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                                <p className="font-12 regular-text grey-text mb-1">Duration</p>
                                                <p className="font-12 semibold-text mb-0">{selectedSlot.duration}</p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer border-0">
                            <button className="btn hive-btn-secondary me-3" onClick={onClose} disabled={loading}>Cancel</button>
                            <button
                                className="btn hive-btn-primary"
                                onClick={handleConfirmReschedule}
                                disabled={!selectedSlot || selectedSlot.seatClass === 'seats-closed' || loading}
                            >
                                {loading ? "Rescheduling..." : "Confirm Reschedule"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderRescheduleEmptyState = () => (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog sessions-modal">
                <div className="modal-content">
                    <div className="modal-header">
                        <h6 className="modal-title">Edit Registration - {participant.emp_name}</h6>
                        <button type="button" className="btn-close font-12" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-md-12 my-4">
                                <div className="d-flex flex-column justify-content-center align-items-center">
                                    <img src="/Images/popup-info-icon.svg" alt="info-icon" />
                                    <p className="font-14 semibold-text mt-4 mb-1">No other slots available for this course!</p>
                                    <p className="font-12 regular-text grey-text mb-1">Please contact admin for any further help</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const otherValidSlots = getOtherValidSlots();

    return (
        otherValidSlots.length > 0 ? renderRescheduleModal() : renderRescheduleEmptyState()
    );
};

export default EditParticipants;
